// @ts-strict-ignore
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { LMContextService } from '@services/lm-context.service';
import { SohoAlertDirective } from 'ids-enterprise-ng';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, first, takeUntil } from 'rxjs/operators';
import { LMLplEditorService } from './lm-lpl-editor.service';

@Component({
  selector:    'lm-lpl-editor-compare',
  standalone: false,
  templateUrl: 'lm-lpl-editor-compare.component.html',
  styleUrls:   [ 'lm-lpl-editor-compare.component.scss' ],
})

export class LMLplEditorCompareComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild(SohoAlertDirective) alert: SohoAlertDirective;
  @ViewChild('editorContainer') _editorContainer: ElementRef;

  @Input() set validationOptions(validationOptions: LMEditorValidationOptions) {
    this._validationOptions = validationOptions;
    this.canValidateLpl = this.lmLplEditorService.canValidateLpl(validationOptions);
  }

  @Input() uniqueId: string;

  get validationOptions(): LMEditorValidationOptions {
    return this._validationOptions;
  }

  get original(): string {
    return this._original;
  }

  @Input() set original(lpl: string) {
    this._original = lpl;
    if (this.diffEditor) {
        if (lpl?.length > 0) {
          this.diffEditor.getOriginalEditor().getModel().setValue(this.original);
        } else {
          this.diffEditor.getOriginalEditor().getModel().setValue('');
        }
    }
  }

  get lpl(): string {
    return this._lpl;
  }

  @Input() set lpl(lpl: string) {
    this._lpl = lpl;
    if (this.diffEditor) {
      if (lpl?.length > 0) {
        this.diffEditor.getModifiedEditor().getModel().setValue(this.lpl);
      } else {
        this.diffEditor.getModifiedEditor().getModel().setValue('');
      }
    }
  }

  @Input() set readonly(readonly: boolean) {
    this._readonly = readonly;
    this.updateReadOnlyState();
  }

  get readonly(): boolean {
    return this._readonly;
  }

  @Output() lplChangedEvent = new EventEmitter<any>();

  private destroyer$ = new Subject<void>();
  public lplValidator$ = new Subject<string>();
  private diffEditor: monaco.editor.IDiffEditor;
  private _validationOptions: LMEditorValidationOptions;
  public canValidateLpl = false;
  private _readonly = false;
  private _original: string;
  private _lpl: string;
  public validatorTooltip: string;
  showWhiteSpaceChanges: boolean;

  public monacoOptions: monaco.editor.IStandaloneDiffEditorConstructionOptions = {
    theme:                'vs',
    automaticLayout:      true,
    foldingStrategy:      'indentation',
    dragAndDrop:          true,
    scrollBeyondLastLine: false,
    ignoreTrimWhitespace: true
  };

  constructor(
    protected lmContextService: LMContextService,
    protected lmLplEditorService: LMLplEditorService) {
    // no-op
  }

  ngOnInit(): void {
    this.validatorTooltip = Soho.Locale.translate('LPL_EDITOR_VALIDATOR_TOOLTIP');
  }

  ngAfterViewInit(): void {
    this.initMonaco();

    this.lplValidator$.pipe(
      takeUntil(this.destroyer$),
      debounceTime(1250),
      distinctUntilChanged())
      .subscribe(value => {
        this.validateLpl();
      });
  }

  ngOnDestroy(): void {
    this.destroyer$.next();
    this.destroyer$.complete();

    if (this.diffEditor) {
      this.diffEditor.dispose();
    }
  }
  private initMonaco() {
    if (!this.lmLplEditorService.isMonacoAvailable()) {
      this.lmLplEditorService.loadingMonacoFinished.pipe(first()).subscribe(() => {
        this.initMonaco();
      });
      return;
    }

    this.diffEditor = monaco.editor.createDiffEditor(this._editorContainer.nativeElement, this.monacoOptions);

    this.diffEditor.setModel({
      original: monaco.editor.createModel(this.original, 'lpl'),
      modified: monaco.editor.createModel(this.lpl, 'lpl')
    });

    this.lmLplEditorService.syncMonacoThemeToSohoTheme();
    this.updateReadOnlyState();
    this.addMonacoListeners();
  }

  private updateReadOnlyState(): void {
    if (this.diffEditor) {
      this.diffEditor.updateOptions({ readOnly: this.readonly, extraEditorClassName: this.readonly ? 'lm-readonly-editor' : '' });
    }
  }

  private addMonacoListeners(): void {
    const model = this.diffEditor.getModifiedEditor().getModel() as monaco.editor.ITextModel;
    model.onDidChangeContent((e) => {
      if (!this.readonly) {
        const changedLPL = this.diffEditor.getModifiedEditor().getModel().getValue();
        this.lplChangedEvent.emit(changedLPL);
        this.lplValidator$.next(changedLPL);
      }
    });
  }

  private validateLpl(): void {
    if (this.readonly) {
      return;
    }

    if (this.validationOptions) {
      this._validateLpl().subscribe((response: LPLValidationResponse) => {
        this.updateLPLValidation(response);
      });
    }
  }

  private _validateLpl(): Observable<LPLValidationResponse> {
    const changedLPL = this.diffEditor.getModifiedEditor().getModel().getValue();
    return this.lmLplEditorService.validateLpl(changedLPL, this.validationOptions);
  }

  private updateLPLValidation(lplValidationResponse?: LPLValidationResponse): void {
    const model = this.diffEditor.getModifiedEditor().getModel() as monaco.editor.ITextModel;
    this.lmLplEditorService.updateLPLValidation(model, lplValidationResponse, this.alert);
  }

  onMonacoClickEvent(id: string) {
    this.diffEditor.focus();

    if ('next' === id || "previous" === id) {
      this.diffEditor.goToDiff(id);
      return;
    }

    const actions: monaco.editor.IEditorAction[] = this.diffEditor.getSupportedActions();
    const action = actions.find(item => item.id === id && item.isSupported());

    if (action) {
      action.run();
    }
  }

  handleShowWhiteSpaceChanges(show: boolean) {
    this.monacoOptions.ignoreTrimWhitespace = !show;
    this.diffEditor.updateOptions(this.monacoOptions);
  }

  protected readonly Soho = Soho;
}
